import { MeshLambertMaterial, Vector3 } from "three";
import { ATMOSPHERE_GLSL, CLOUD_COVER_GLSL, CLOUD_MAX_ALPHA, CLOUD_LIGHTING_GLSL, MOONLIGHT_GLSL, SPHERE_UV_GLSL, moonUniforms } from "./earth-lighting";

// Based off: http://stemkoski.blogspot.fr/2013/07/shaders-in-threejs-glow-and-halo.html
// sunDir: a shared { value: Vector3 } uniform (world space) for twilight tinting.
// shared: the Earth's lighting uniforms (earth-lighting.js), for the moon,
// the sun in object space and city lights under the clouds.
export function createCloudMaterial(hexpos, texture, sunDir = { value: new Vector3(1, 0, 0) }, shared = {
  ...moonUniforms(),
  uSunDirObject: { value: new Vector3(1, 0, 0) },
  uNight: { value: null },
  uCityRadiance: { value: 0.0 },
  uCloudsPrev: { value: null },
  uCloudsMix: { value: 1.0 },
  uWater: { value: null },
  uCloudWindow: { value: 1.0 },
}) {
  // Coverage (alpha × grey, see cloudCover) is read from `map` here rather
  // than through an alphaMap, so it can be cross-faded with uCloudsPrev.
  var material = new MeshLambertMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,   // prevents cloud seam corrupting depth buffer for glow
  });

  material.userData.hexpos = { value: hexpos };

  material.onBeforeCompile = function (shader) {
    shader.uniforms.hexpos = material.userData.hexpos;
    shader.uniforms.uSunDir = sunDir;
    for (const name of ["uMoonDir", "uMoonIrradiance", "uNightFloor", "uSunDirObject", "uNight", "uCityRadiance", "uCloudsPrev", "uCloudsMix", "uWater", "uCloudWindow"]) {
      shader.uniforms[name] = shared[name];
    }
    shader.uniforms.uTime = { value: 0.0 };
    shader.uniforms.uPressTime = { value: 1e10 };
    shader.uniforms.uUnpressTime = { value: 1e10 };
    shader.uniforms.uGlobalFade = { value: 1.0 };
    shader.uniforms.uPrevSelectedPos = { value: new Vector3(1e6, 1e6, 1e6) };
    material.userData.uTime = shader.uniforms.uTime;
    material.userData.uPressTime = shader.uniforms.uPressTime;
    material.userData.uUnpressTime = shader.uniforms.uUnpressTime;
    material.userData.uGlobalFade = shader.uniforms.uGlobalFade;
    shader.uniforms.uZoomFade = { value: 1.0 };
    material.userData.uZoomFade = shader.uniforms.uZoomFade;
    material.userData.uPrevSelectedPos = shader.uniforms.uPrevSelectedPos;

    shader.vertexShader =
      "varying vec3 vVertexWorldPosition;\nvarying vec3 vCloudObjectNormal;\n" + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace(
      "vViewPosition = - mvPosition.xyz;",
      `
      vViewPosition = - mvPosition.xyz;
      vVertexWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
      vCloudObjectNormal = normalize(position);
    `
    );

    shader.fragmentShader =
      "varying vec3 vVertexWorldPosition;\nuniform vec3 uSunDir;\nuniform vec3 hexpos;\nuniform vec3 uPrevSelectedPos;\nuniform float uTime;\nuniform float uPressTime;\nuniform float uUnpressTime;\nuniform float uGlobalFade;\nuniform float uZoomFade;\nuniform vec3 uMoonDir;\nuniform float uMoonIrradiance;\nuniform float uNightFloor;\nvarying vec3 vCloudObjectNormal;\nuniform vec3 uSunDirObject;\nuniform sampler2D uNight;\nuniform float uCityRadiance;\nuniform sampler2D uCloudsPrev;\nuniform float uCloudsMix;\nuniform sampler2D uWater;\nuniform float uCloudWindow;\n" + ATMOSPHERE_GLSL + SPHERE_UV_GLSL + CLOUD_COVER_GLSL + MOONLIGHT_GLSL +
      shader.fragmentShader;

    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <map_fragment>",
      `
      #include <map_fragment>
      // Alpha and grey (whose product is the cover) of the map, cross-faded
      // from the previous one while uCloudsMix < 1.
      float a = sampledDiffuseColor.a;
      float cloudGrey = sampledDiffuseColor.g;
      if (uCloudsMix < 1.0) {
        vec4 prevTexel = texture2D(uCloudsPrev, vMapUv);
        a = mix(prevTexel.a, a, uCloudsMix);
        cloudGrey = mix(prevTexel.g, cloudGrey, uCloudsMix);
      }
      // The live map carries a thin grey veil over much of the globe (and
      // the polar regions), which reads as a second, hazy layer under the
      // real clouds; this curve thins it and keeps thick cloud opaque.
      a = smoothstep(0.35, 0.95, a);
      
      // Clearance mask: fade out clouds around the selected hex
      float cloudClearance = 1.0;
      if (length(hexpos) > 1.0) {
        float age = uTime - uPressTime;
        float t = clamp(age / 0.45, 0.0, 1.0);
        float expansion = t * t;
        float d = distance(normalize(vVertexWorldPosition), normalize(hexpos));
        float clearance = smoothstep(0.05 * expansion, 0.25 * expansion, d);
        // Tie clearance intensity to uGlobalFade (1.0 when dragging = no clearance)
        clearance = mix(1.0, clearance, 1.0 - (uGlobalFade - 0.2) / 0.8);
        cloudClearance *= mix(1.0, clearance, expansion);
      }
      if (length(uPrevSelectedPos) > 1.0) {
        float unpressAge = uTime - uUnpressTime;
        if (unpressAge >= 0.0 && unpressAge < 0.45) {
          float t = 1.0 - clamp(unpressAge / 0.45, 0.0, 1.0);
          float expansion = t * t;
          float d = distance(normalize(vVertexWorldPosition), normalize(uPrevSelectedPos));
          float clearance = smoothstep(0.05 * expansion, 0.25 * expansion, d);
          // Tie clearance intensity to uGlobalFade
          clearance = mix(1.0, clearance, 1.0 - (uGlobalFade - 0.2) / 0.8);
          cloudClearance *= mix(1.0, clearance, expansion);
        }
      }
      // A thinner window over land where the camera looks, and thick cloud
      // capped, so the land stays readable from orbit.
      a = min(a, ${CLOUD_MAX_ALPHA.toFixed(2)}) * cloudViewWindow(vVertexWorldPosition, cameraPosition, landCover(uWater, vMapUv), uCloudWindow);
      a *= cloudClearance * uZoomFade;
      
      ${CLOUD_LIGHTING_GLSL}
      diffuseColor = vec4(cloudTint, min(1.0, a) * cloudGrey);
    `
    );

    // Cover at any point (cloud-top relief in CLOUD_LIGHTING_GLSL), also
    // cross-faded; each map at its own mip level for the same footprint.
    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <map_pars_fragment>",
      `
      #include <map_pars_fragment>
      float cloudCoverAt(vec2 uv, float lod) {
        float cover = cloudCover(textureLod(map, uv, lod));
        if (uCloudsMix < 1.0) {
          float prevLod = max(0.0, lod + log2(float(textureSize(uCloudsPrev, 0).x) / float(textureSize(map, 0).x)));
          cover = mix(cloudCover(textureLod(uCloudsPrev, uv, prevLod)), cover, uCloudsMix);
        }
        return cover;
      }
    `
    );

    /* shader.fragmentShader = shader.fragmentShader.replace(
      "#include <map_fragment>",
      `
      #include <map_fragment>
      float fade = min(1.0, length(hexpos));
      float dist = distance(normalize(vVertexWorldPosition), normalize(hexpos));
      float dist2 = distance(normalize(hexpos), normalize(cameraPosition));
      fade = fade*(1.0-min(1.0,pow(dist2*10.0,3.0)));
      float a = diffuseColor.a*mix(1.0, min(1.0, pow(dist*2.0,3.0)),fade );// + vec4(1.0,1.0,1.0,pow(1.0-min(1.0,dist*5.0),2.0);
      diffuseColor =  vec4( 1.0,1.0,1.0, min(1.0, a)) ;
    `
    ); */
    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <emissivemap_fragment>",
      `
      #include <emissivemap_fragment>
      totalEmissiveRadiance += cloudEmission;
      //totalEmissiveRadiance += vec3(1.0,1.0,1.0)*pow(1.0-min(1.0,dist*5.0),2.0);
    `
    );
  };

  return material;
}
